import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { services as s } from "@/services";
import type { ActionViewT } from "@/types/database/actions";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { EntryViewT } from "@/types/database/entries";
import type { ExpandedSchedule } from "@/types/database/schedule";
import type { ExtendedTrackerViewT } from "@/types/database/trackers";
import dayjs from "dayjs";
import { Calendar, Contact2 } from "lucide-react";
import { FormInput } from "lucide-react";
import { useState } from "react";
import TrackerChart from "../../charts/TrackerChart";
import ActionCard from "../../diaries/features/history/components/ActionCard";
import EntryCard from "../../entries/components/EntryCard";
import { sortEntryValuesView } from "../../entries/services/sortEntryValuesView";
import SchedulePreview from "../../schedules/components/SchedulePreview";
import EditTracker from "./EditTracker";

interface TrackerInHistoryProps {
	tracker: ExtendedTrackerViewT;
	index: number;
	actions: ActionViewT[];
	sortAscending: boolean;
}

const TrackerInHistory = ({
	tracker,
	index,
	actions,
	sortAscending,
}: TrackerInHistoryProps) => {
	const hasEntries = tracker.entries_view && tracker.entries_view.length > 0;

	const [isEditingTracker, setIsEditingTracker] = useState(false);
	return (
		<div
			key={tracker.tracker_id}
			className="space-y-2 bg-white dark:bg-slate-900 shadow-sm px-4 py-4 border rounded-lg w-full"
		>
			{/* Tracker Header */}
			<div className="mb-4">
				<div className="flex flex-row justify-between items-start w-full">
					<h3 className="mb-2 font-semibold text-xl">
						{tracker.tracker_name ?? `Intervention #${index + 1}`}
					</h3>
					<Button
						onClick={() => setIsEditingTracker(!isEditingTracker)}
						variant="link"
						className="my-0 px-2 py-0 w-fit h-fit"
					>
						{isEditingTracker ? "Done" : "Edit"}
					</Button>
				</div>
				{isEditingTracker ? (
					<EditTracker tracker={tracker} />
				) : (
					<p className="flex items-center gap-2 text-muted-foreground text-sm">
						{s.time.formatDate(tracker.start_date)} -{" "}
						{tracker.end_date === null
							? "Now"
							: s.time.formatDate(tracker.end_date)}
					</p>
				)}
			</div>
			<Separator />
			{/* Events section removed and moved to HistoryByTracker */}
			<div className="">
				{hasEntries ? (
					<>
						<TrackerChart tracker={tracker} />
						<Accordion collapsible type="single">
							<AccordionItem value="entries">
								<AccordionTrigger className="text-sm">
									<span>See Diary History</span>
								</AccordionTrigger>
								<AccordionContent className="space-y-2">
									{[
										...tracker.entries_view.map((entry) => ({
											...entry,
											entry_values: sortEntryValuesView(entry.entry_values),
											type: "entry",
											time: entry.calculated_entry_time,
											id: entry.entry_id,
										})),
										...actions.map((a) => ({
											...a,
											type: "action",
											time: a.action_time,
											id: a.action_id,
										})),
									]
										.sort((a, b) =>
											sortAscending
												? dayjs(a.time).diff(dayjs(b.time))
												: dayjs(b.time).diff(dayjs(a.time)),
										)
										.map((log) =>
											log.type === "entry" ? (
												<EntryCard
													editable={false}
													key={log.id}
													entry={log as unknown as EntryViewT}
													size="sm"
												/>
											) : (
												<ActionCard key={log.id} action={log as ActionViewT} />
											),
										)}
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</>
				) : (
					<div className="flex flex-col justify-center items-center py-4 text-muted-foreground">
						<p className="text-sm">No entries yet</p>
					</div>
				)}
			</div>
			<Accordion collapsible type="single">
				<AccordionItem value="tracker-details">
					<AccordionTrigger className="text-sm">
						Show More Details
					</AccordionTrigger>
					<AccordionContent className="space-y-2 pt-2">
						<div className="space-y-1">
							<p className="flex items-center gap-2 font-medium text-slate-600 text-sm">
								<Contact2 size={20} />
								Contact Person
							</p>
							<p>{tracker.contact_name}</p>
						</div>
						<Separator />

						{/* Form Details */}
						{tracker.form && (
							<div className="space-y-2 mb-4">
								<div className="flex items-center gap-2 font-medium text-slate-600 text-sm">
									<FormInput size={20} />
									Forms
								</div>
								<div>
									<p className="text-md text-slate-800">
										{tracker.form.form_name}
									</p>
									<p className="text-slate-500 text-sm">
										{tracker.form.form_description}
									</p>
								</div>
							</div>
						)}

						{/* Schedules */}
						{/* Show backup schedule first since it's the initial schedule */}

						{dayjs(tracker.start_date).day !==
							dayjs(tracker.reference_date).day &&
							tracker.back_up_schedules && (
								<>
									<Separator />
									<div className="flex items-center gap-2 font-medium text-slate-600 text-sm">
										<Calendar size={20} />
										Initial monitoring schedule ($
										{s.time.formatDate(tracker.start_date)} until
										{s.time.formatDate(tracker.reference_date)})
									</div>
									<SchedulePreview
										title={`Initial monitoring schedule (${s.time.formatDate(
											tracker.start_date,
										)} until ${s.time.formatDate(tracker.reference_date)})`}
										schedule={tracker.back_up_schedules as ExpandedSchedule}
									/>
								</>
							)}
						<Separator />
						<div className="flex items-center gap-2 font-medium text-slate-600 text-sm">
							<Calendar size={20} />
							Monitoring schedule
							<br />
							(Starting {s.time.formatDate(tracker.reference_date)})
						</div>
						<SchedulePreview
							title={`Monitoring schedule (starting ${s.time.formatDate(
								tracker.reference_date,
							)})`}
							schedule={tracker.schedules as ExpandedSchedule}
						/>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

export default TrackerInHistory;
